import { ComponentProps, ReactNode, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import './Tooltip_.scss';

import { ScrollBar } from '@components/ScrollBar';

export enum TooltipPlace {
    Top = 'top',
    TopStart = 'top-start',
    TopEnd = 'top-end',
    Right = 'right',
    RightStart = 'right-start',
    RightEnd = 'right-end',
    Bottom = 'bottom',
    BottomStart = 'bottom-start',
    BottomEnd = 'bottom-end',
    Left = 'left',
    LeftStart = 'left-start',
    LeftEnd = 'left-end',
}

export enum TooltipType {
    Light = 'light',
    Dark = 'dark',
    Info = 'info',
    Card = 'card',
}

export enum TooltipEffect {
    Float = 'float',
    Solid = 'solid',
}

type EventMap = Record<string, boolean>;
type EventInput = string | string[] | EventMap;
type LegacyOffset = Partial<Record<'top' | 'right' | 'bottom' | 'left', number>>;
type ReactTooltipProps = ComponentProps<typeof ReactTooltip>;

export interface TooltipProps extends Omit<
    ReactTooltipProps,
    'id' | 'children' | 'content' | 'variant' | 'float' | 'hidden' | 'offset'
> {
    title: ReactNode;
    titleId?: string;
    link?: string;
    children: ReactNode;
    type?: TooltipType;
    effect?: TooltipEffect;
    disable?: boolean;
    event?: EventInput;
    eventOff?: EventInput;
    globalEventOff?: EventInput;
    hidden?: boolean;
    variant?: ReactTooltipProps['variant'];
    offset?: number | LegacyOffset;
}

interface Props extends TooltipProps {}

function hasTooltipContent(content: ReactNode): boolean {
    return !(content === '' || content === null || content === undefined);
}

export function Tooltip({
    title,
    link,
    titleId,
    children,
    type = TooltipType.Light,
    effect = TooltipEffect.Solid,
    disable,
    event,
    eventOff,
    globalEventOff,
    variant,
    hidden,
    offset,
    delayHide = 0,
    ...props
}: Props) {
    const refId = useRef<string>(uuid());
    const tooltipId = encodeURI(titleId ?? `tooltip-${refId.current}`);

    const tooltipVariant = variant ?? type;

    const isTooltipFloat = effect === TooltipEffect.Float;
    const isTooltipHidden = Boolean(disable || hidden || !hasTooltipContent(title));
    const tooltipOffset = typeof offset === 'number' ? offset : isTooltipFloat ? 20 : 5;

    return (
        <>
            <div className={'component_tooltip__root'}>
                <ReactTooltip
                    {...props}
                    id={tooltipId}
                    className={tooltipVariant ? `type-${tooltipVariant}` : undefined}
                    float={isTooltipFloat}
                    variant={variant}
                    hidden={isTooltipHidden}
                    offset={tooltipOffset}
                >
                    {!!link ? (
                        <a
                            href={link}
                            target={'_blank'}
                            rel={'noopener'}
                            className={'component_tooltip__link'}
                        >
                            {title}
                        </a>
                    ) : (
                        <ScrollBar
                            hide
                            autoHeight
                        >
                            {title}
                        </ScrollBar>
                    )}
                </ReactTooltip>
            </div>
            <div
                data-tooltip-id={tooltipId}
                data-tooltip-content={typeof title === 'string' ? title : undefined}
            >
                {children}
            </div>
        </>
    );
}
